import React, { useState } from 'react';
import { PeriodoVacaciones, Empleado } from '../tipos/vacaciones';
import './CalendarioLicencias.css';

interface PropiedadesCalendarioLicencias {
  periodos: PeriodoVacaciones[];
  empleados: Empleado[];
}

interface DiaCalendario {
  fecha: Date;
  esMesActual: boolean;
  esHoy: boolean;
  tieneLicencia: boolean;
  tipoLicencia?: string;
  nombreEmpleado?: string;
  departamentoEmpleado?: string;
  descripcion?: string;
  licenciasMultiples?: boolean;
  cantidadLicencias?: number;
  infoEmpleados?: Array<{nombre: string, departamento: string, cargo: string, tipo: string}>;
}

const CalendarioLicencias: React.FC<PropiedadesCalendarioLicencias> = ({ periodos, empleados }) => {
  const [fechaActual, setFechaActual] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [anioSeleccionado, setAnioSeleccionado] = useState<number>(new Date().getFullYear());


  const nombresMeses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const obtenerColorTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'vacaciones';
      case 'Licencia médica':
        return 'medica';
      case 'Día de Beneficio':
        return 'especial';
      case 'Compensatorio':
        return 'compensatorio';
      default:
        return 'vacaciones';
    }
  };

  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'fas fa-umbrella-beach';
      case 'Licencia médica':
        return 'fas fa-heartbeat';
      case 'Día de Beneficio':
        return 'fas fa-star';
      case 'Compensatorio':
        return 'fas fa-clock';
      default:
        return 'fas fa-calendar';
    }
  };

  const generarCalendario = (): DiaCalendario[][] => {
    const anio = fechaActual.getFullYear();
    const mes = fechaActual.getMonth();
    const hoy = new Date();

    const primerDia = new Date(anio, mes, 1);
    const fechaInicio = new Date(primerDia);
    fechaInicio.setDate(fechaInicio.getDate() - primerDia.getDay());

    const semanas: DiaCalendario[][] = [];
    let semana: DiaCalendario[] = [];

    for (let i = 0; i < 42; i++) {
      const fecha = new Date(fechaInicio);
      fecha.setDate(fechaInicio.getDate() + i);

      // Buscar todas las licencias en esta fecha
      const periodosEnFecha = periodos.filter(p => {
        const inicio = new Date(p.fechaInicio);
        const fin = new Date(p.fechaFin);
        const estaEnRango = fecha >= inicio && fecha <= fin;
        const esAnioCorrejo = p.anio === anioSeleccionado;

        return estaEnRango && esAnioCorrejo;
      });

      // Obtener información completa de empleados
      const infoEmpleados = periodosEnFecha.map(p => {
        const empleado = empleados.find(e => e.id === p.idEmpleado);
        return {
          nombre: p.nombreEmpleado,
          departamento: empleado?.departamento || 'N/A',
          cargo: empleado?.cargo || 'N/A',
          tipo: p.tipo
        };
      });

      const dia: DiaCalendario = {
        fecha: new Date(fecha),
        esMesActual: fecha.getMonth() === mes,
        esHoy: fecha.toDateString() === hoy.toDateString(),
        tieneLicencia: periodosEnFecha.length > 0,
        tipoLicencia: periodosEnFecha[0]?.tipo,
        nombreEmpleado: periodosEnFecha.length === 1 ? periodosEnFecha[0].nombreEmpleado : undefined,
        departamentoEmpleado: periodosEnFecha.length === 1 ? infoEmpleados[0]?.departamento : undefined,
        descripcion: periodosEnFecha[0]?.descripcion,
        licenciasMultiples: periodosEnFecha.length > 1,
        cantidadLicencias: periodosEnFecha.length,
        infoEmpleados: periodosEnFecha.length > 1 ? infoEmpleados : undefined
      };

      semana.push(dia);

      if (semana.length === 7) {
        semanas.push(semana);
        semana = [];
      }
    }

    return semanas;
  };

  const navegarMes = (direccion: 'prev' | 'next') => {
    setFechaActual(anterior => {
      const nuevaFecha = new Date(anterior);
      nuevaFecha.setMonth(anterior.getMonth() + (direccion === 'next' ? 1 : -1));
      return nuevaFecha;
    });
  };

  const irAHoy = () => {
    setFechaActual(new Date());
  };

  // Generar opciones de año dinámicamente: desde 2 años atrás hasta 1 año adelante
  const anioActual = new Date().getFullYear();
  const opcionesAnio = Array.from({ length: 4 }, (_, i) => anioActual - 2 + i);
  const semanas = generarCalendario();

  // Estadísticas del año seleccionado
  const estadisticasAnio = periodos
    .filter(p => p.anio === anioSeleccionado)
    .reduce((acumulador, periodo) => {
      switch (periodo.tipo) {
        case 'Vacaciones':
          acumulador.vacaciones += periodo.dias;
          break;
        case 'Licencia médica':
          acumulador.licenciamédica += periodo.dias;
          break;
        case 'Día de Beneficio':
          acumulador.licenciaespecial += periodo.dias;
          break;
        case 'Compensatorio':
          acumulador.compensatorio += periodo.dias;
          break;
      }
      acumulador.total += periodo.dias;
      return acumulador;
    }, { total: 0, vacaciones: 0, licenciamédica: 0, licenciaespecial: 0, compensatorio: 0 });

  return (
    <div className="license-calendar">
      <div className="calendar-header">
        <div className="calendar-controls">
          <button
            className="btn-icon"
            onClick={() => navegarMes('prev')}
            title="Mes anterior"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <div className="month-year">
            <h2>{nombresMeses[fechaActual.getMonth()]} {fechaActual.getFullYear()}</h2>
          </div>

          <button
            className="btn-icon"
            onClick={() => navegarMes('next')}
            title="Mes siguiente"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="year-controls">
          <button className="btn-today" onClick={irAHoy}>
            Hoy
          </button>

          <div className="year-selector">
            <label htmlFor="year-select">Año:</label>
            <select
              id="year-select"
              value={anioSeleccionado}
              onChange={(e) => setAnioSeleccionado(Number(e.target.value))}
            >
              {opcionesAnio.map(anio => (
                <option key={anio} value={anio}>{anio}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header-row">
          {diasSemana.map(dia => (
            <div key={dia} className="calendar-header-cell">
              {dia}
            </div>
          ))}
        </div>

        {semanas.map((semana, indiceSemana) => (
          <div key={indiceSemana} className="calendar-week">
            {semana.map((dia, indiceDia) => (
              <div
                key={indiceDia}
                className={`calendar-day ${dia.esMesActual ? 'current-month' : 'other-month'}
                          ${dia.esHoy ? 'today' : ''}
                          ${dia.tieneLicencia ? `license ${dia.licenciasMultiples ? 'multiple' : obtenerColorTipo(dia.tipoLicencia!)}` : ''}`}
                title={dia.tieneLicencia ?
                  dia.licenciasMultiples ?
                    `${dia.cantidadLicencias} empleados con licencia:\n${dia.infoEmpleados?.map(emp => `• ${emp.nombre} (${emp.cargo} - ${emp.departamento}) - ${emp.tipo}`).join('\n')}` :
                    `${dia.nombreEmpleado} (${dia.departamentoEmpleado}) - ${dia.tipoLicencia}` : ''}
              >
                <span className="day-number">{dia.fecha.getDate()}</span>
                {dia.tieneLicencia && (
                  <div className="license-content">
                    <div className="license-indicator">
                      {dia.licenciasMultiples ? (
                        <span className="multiple-count">{dia.cantidadLicencias}</span>
                      ) : (
                        <i className={obtenerIconoTipo(dia.tipoLicencia!)}></i>
                      )}
                    </div>
                    {!dia.licenciasMultiples && dia.nombreEmpleado && (
                      <div className="employee-info-in-day">
                        <div className="employee-name-in-day">
                          {dia.nombreEmpleado}
                        </div>
                        <div className="employee-dept-in-day">
                          {dia.departamentoEmpleado}
                        </div>
                      </div>
                    )}
                    {dia.licenciasMultiples && dia.infoEmpleados && (
                      <div className="multiple-employees-list">
                        {dia.infoEmpleados.slice(0, 3).map((emp, indice) => (
                          <div key={indice} className="employee-item-in-day">
                            <div className="employee-name-in-day">
                              {emp.nombre}
                            </div>
                            <div className="employee-position-in-day">
                              {emp.cargo}
                            </div>
                          </div>
                        ))}
                        {dia.infoEmpleados.length > 3 && (
                          <div className="more-employees">
                            +{dia.infoEmpleados.length - 3} más
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="calendar-info">
        <div className="calendar-legend">
          <h3>Tipos de Licencia</h3>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-color vacaciones"></div>
              <i className="fas fa-umbrella-beach"></i>
              <span>Vacaciones</span>
            </div>
            <div className="legend-item">
              <div className="legend-color medica"></div>
              <i className="fas fa-heartbeat"></i>
              <span>Licencia médica</span>
            </div>
            <div className="legend-item">
              <div className="legend-color especial"></div>
              <i className="fas fa-star"></i>
              <span>Día de Beneficio</span>
            </div>
            <div className="legend-item">
              <div className="legend-color compensatorio"></div>
              <i className="fas fa-clock"></i>
              <span>Compensatorio</span>
            </div>
            <div className="legend-item">
              <div className="legend-color multiple"></div>
              <i className="fas fa-users"></i>
              <span>Múltiples empleados</span>
            </div>
          </div>
        </div>

        <div className="year-summary">
          <h3>Resumen {anioSeleccionado}</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-value">{estadisticasAnio.total}</span>
              <span className="summary-label">Total días</span>
            </div>
            <div className="summary-item">
              <span className="summary-value">{estadisticasAnio.vacaciones}</span>
              <span className="summary-label">Vacaciones</span>
            </div>
            <div className="summary-item">
              <span className="summary-value">{estadisticasAnio.licenciamédica}</span>
              <span className="summary-label">Médicas</span>
            </div>
            <div className="summary-item">
              <span className="summary-value">{estadisticasAnio.compensatorio}</span>
              <span className="summary-label">Compensatorio</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarioLicencias;